const SEARCH_DEBOUNCE_MS = 250;
const TOAST_DURATION_MS = 4000;
const LINE_COLLAPSE_MS = 240;
const CART_SECTION_ID = 'cart-drawer';
const RECENT_STORAGE_KEY = 'ariko:recently-viewed';
const RECENT_MAX_STORED = 12;
const WEEKEND_DAYS = [0, 6];

const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...options.headers },
  });
  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || window.theme.strings.cartError);
  }
  return data;
};

const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

const TOAST_EXIT_MS = 200;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const setToastPart = (toast, selector, value) => {
  const el = toast.querySelector(selector);
  if (!el) return;
  el.textContent = value || '';
  el.hidden = !value;
};

const hideToast = () => {
  const toast = document.querySelector('[data-toast]');
  if (!toast || toast.hidden) return;
  clearTimeout(showToast.timer);
  clearTimeout(showToast.exitTimer);
  const finish = () => {
    toast.hidden = true;
    toast.classList.remove('is-visible', 'is-leaving');
  };
  if (reducedMotion()) { finish(); return; }
  toast.classList.add('is-leaving');
  showToast.exitTimer = setTimeout(finish, TOAST_EXIT_MS);
};

const scheduleToastDismiss = () => {
  clearTimeout(showToast.timer);
  showToast.timer = setTimeout(hideToast, TOAST_DURATION_MS);
};

/**
 * Show the site notification. Accepts a plain string (error/info) or an object:
 * { type: 'added' | 'removed', title, variant, image, status, showCart }.
 * Rapid calls replace the previous notification (single element, timers reset).
 */
const showToast = (message) => {
  const toast = document.querySelector('[data-toast]');
  if (!toast) return;
  const options = typeof message === 'string' ? { status: message, type: 'info' } : message;
  const strings = window.theme.strings.notification || {};
  const status = options.status || (options.type === 'removed' ? strings.removed : strings.added);

  clearTimeout(showToast.exitTimer);
  toast.classList.remove('is-leaving');
  toast.dataset.type = options.type || 'info';
  setToastPart(toast, '[data-toast-status]', status);
  setToastPart(toast, '[data-toast-title]', options.title);
  setToastPart(toast, '[data-toast-variant]', options.variant);
  const media = toast.querySelector('[data-toast-image]');
  if (media) {
    media.innerHTML = options.image ? `<img src="${options.image}" alt="" width="64" height="64" loading="eager" decoding="async">` : '';
    media.hidden = !options.image;
  }
  const action = toast.querySelector('[data-toast-action]');
  if (action) action.hidden = !options.showCart;

  toast.hidden = false;
  // Restart the enter animation when replacing a visible toast.
  toast.classList.remove('is-visible');
  void toast.offsetWidth;
  toast.classList.add('is-visible');
  scheduleToastDismiss();

  if (!showToast.bound) {
    showToast.bound = true;
    toast.addEventListener('click', (event) => {
      if (event.target.closest('[data-toast-close]')) hideToast();
      if (event.target.closest('[data-toast-action]')) {
        hideToast();
        if (!document.querySelector('cart-drawer')) window.location.href = window.theme.routes.cart;
      }
    });
    ['mouseenter', 'focusin'].forEach((type) => toast.addEventListener(type, () => clearTimeout(showToast.timer)));
    ['mouseleave', 'focusout'].forEach((type) => toast.addEventListener(type, () => { if (!toast.hidden) scheduleToastDismiss(); }));
  }
};

/** Build toast options from a /cart/add.js response (single line item or { items }). */
const toastFromAddResponse = (data) => {
  const item = data?.items ? data.items[0] : data;
  if (!item) return null;
  const image = item.featured_image?.url || item.image;
  return {
    type: 'added',
    title: item.product_title,
    variant: item.product_has_only_default_variant ? '' : item.variant_title,
    image: image ? `${image}${image.includes('?') ? '&' : '?'}width=120` : '',
    showCart: true,
  };
};

const bumpCartIcon = () => {
  if (reducedMotion()) return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    const target = el.closest('a, button') || el;
    target.classList.remove('is-bumping');
    void target.offsetWidth;
    target.classList.add('is-bumping');
    target.addEventListener('animationend', () => target.classList.remove('is-bumping'), { once: true });
  });
};

const updateCartCount = (count) => {
  const previous = Number(document.querySelector('[data-cart-count]')?.textContent || 0);
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = Number(count) === 0;
  });
  if (Number(count) > previous) bumpCartIcon();
};

const SUCCESS_FLASH_MS = 1800;

const flashSuccess = (button) => {
  if (!button || button.dataset.state) return;
  const original = button.innerHTML;
  button.dataset.state = 'success';
  if (!reducedMotion()) {
    button.classList.add('is-pulsing');
    button.addEventListener('animationend', () => button.classList.remove('is-pulsing'), { once: true });
  }
  button.innerHTML = `<svg class="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg><span>${window.theme.strings.added}</span>`;
  setTimeout(() => {
    button.innerHTML = original;
    delete button.dataset.state;
  }, SUCCESS_FLASH_MS);
};

const withViewTransition = (update) => {
  if (!document.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    update();
    return;
  }
  document.startViewTransition(update);
};

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog?.addEventListener('close', () => { document.documentElement.style.overflow = ''; });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-open-cart]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    });
  }

  open(trigger) {
    this.returnFocus = trigger || document.activeElement;
    if (!this.dialog.open) this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
  }

  close() {
    this.dialog.close();
    document.documentElement.style.overflow = '';
    this.returnFocus?.focus?.();
  }

  onClick(event) {
    if (event.target.closest('[data-close-drawer]')) {
      this.close();
      return;
    }
    const button = event.target.closest('[data-line-quantity]');
    if (!button) return;
    event.preventDefault();
    this.changeLine(button.dataset.line, Number(button.dataset.lineQuantity));
  }

  onChange(event) {
    const input = event.target.closest('input[data-line]');
    if (!input) return;
    this.changeLine(input.dataset.line, Math.max(0, Number(input.value)));
  }

  lineElement(line) {
    return this.querySelector(`[data-line-item="${line}"]`);
  }

  async changeLine(line, quantity) {
    this.setAttribute('aria-busy', 'true');
    const row = this.lineElement(line);
    const removing = quantity === 0 && row;
    // Read product info before the re-render: the row is gone afterwards.
    const removed = removing ? { type: 'removed', title: row.dataset.lineTitle, variant: row.dataset.lineVariant, image: row.dataset.lineImage } : null;
    let collapse = Promise.resolve();
    if (removing && !reducedMotion()) {
      collapse = new Promise((resolve) => {
        row.addEventListener('animationend', resolve, { once: true });
        setTimeout(resolve, LINE_COLLAPSE_MS + 50);
      });
      row.classList.add('is-removing');
    }
    try {
      const [cart] = await Promise.all([
        fetchJSON(window.theme.routes.cartChange + '.js', {
          method: 'POST',
          body: JSON.stringify({ line: Number(line), quantity, sections: CART_SECTION_ID, sections_url: window.location.pathname }),
        }),
        collapse,
      ]);
      if (removing) {
        this.render(cart.sections[CART_SECTION_ID]);
        showToast(removed);
      } else {
        withViewTransition(() => this.render(cart.sections[CART_SECTION_ID]));
        const updated = this.lineElement(line);
        if (updated && !reducedMotion()) {
          updated.classList.add('is-updated');
          updated.addEventListener('animationend', () => updated.classList.remove('is-updated'), { once: true });
        }
      }
      updateCartCount(cart.item_count);
    } catch (error) {
      row?.classList.remove('is-removing');
      showToast(error.message);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  render(html) {
    if (!html) return;
    const fresh = parseHTML(html).querySelector('[data-cart-drawer-inner]');
    const current = this.querySelector('[data-cart-drawer-inner]');
    if (fresh && current) current.replaceWith(fresh);
  }
}
customElements.define('cart-drawer', CartDrawer);

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
    this.querySelector('[data-retry]')?.addEventListener('click', () => this.form.requestSubmit());
  }

  async onSubmit(event) {
    if (event.submitter?.hasAttribute('data-buy-now')) {
      event.submitter.setAttribute('aria-busy', 'true');
      return;
    }
    const drawer = document.querySelector('cart-drawer');
    const appDrawer = window.theme.cartType === 'app';
    if (!appDrawer && (!drawer || window.theme.cartType !== 'drawer')) return;
    event.preventDefault();

    const button = this.form.querySelector('[type="submit"]');
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    this.querySelector('[data-form-error]')?.setAttribute('hidden', '');

    const formData = new FormData(this.form);
    if (!appDrawer) {
      formData.append('sections', CART_SECTION_ID);
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(window.theme.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || window.theme.strings.cartError);

      if (appDrawer) {
        const cart = await fetchJSON(`${window.theme.routes.cart}.js`);
        updateCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'theme' } }));
      } else {
        drawer.render(data.sections[CART_SECTION_ID]);
        const count = drawer.querySelector('[data-cart-drawer-inner]')?.dataset.count;
        if (count !== undefined) updateCartCount(count);
        drawer.open(button);
      }
      const added = toastFromAddResponse(data);
      if (added) showToast({ ...added, showCart: appDrawer });
      flashSuccess(button);
    } catch (error) {
      const errorBox = this.querySelector('[data-form-error]');
      const message = errorBox?.querySelector('[data-form-error-message]');
      if (errorBox && message) {
        message.textContent = error.message;
        errorBox.removeAttribute('hidden');
      } else {
        showToast(error.message);
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}
customElements.define('product-form', ProductForm);

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-step]');
      if (!button) return;
      event.preventDefault();
      const min = Number(this.input.min || 1);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const next = Number(this.input.value || min) + Number(button.dataset.step);
      this.input.value = Math.min(max, Math.max(min, next));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}
customElements.define('quantity-input', QuantityInput);

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange.bind(this));
  }

  async onChange() {
    const selected = Array.from(this.querySelectorAll('input[type="radio"]:checked, select'))
      .map((el) => el.dataset.optionValueId || el.selectedOptions?.[0]?.dataset.optionValueId)
      .filter(Boolean);
    const sectionId = this.dataset.sectionId;
    const url = `${this.dataset.url}?option_values=${selected.join(',')}&section_id=${sectionId}`;

    this.closest('[data-product]')?.setAttribute('aria-busy', 'true');
    try {
      const html = await (await fetch(url)).text();
      const doc = parseHTML(html);
      const variantId = doc.querySelector('[data-selected-variant]')?.dataset.selectedVariant;
      withViewTransition(() => {
        ['[data-price-wrapper]', '[data-buy-buttons]', 'variant-picker', '[data-sku]', '[data-sticky-price]', '[data-sticky-action]', '[data-model]'].forEach((selector) => {
          const next = doc.querySelector(selector);
          const current = document.querySelector(`#MainProduct-${sectionId} ${selector}`);
          if (next && current) current.replaceWith(next);
        });
      });
      if (variantId) {
        const params = new URLSearchParams(window.location.search);
        params.set('variant', variantId);
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
        document.querySelector(`#MainProduct-${sectionId} product-gallery`)?.showVariantMedia(doc.querySelector('[data-featured-media-id]')?.dataset.featuredMediaId);
        document.dispatchEvent(new CustomEvent('variant:change', { detail: readVariantState() }));
      }
    } finally {
      this.closest('[data-product]')?.removeAttribute('aria-busy');
    }
  }
}
customElements.define('variant-picker', VariantPicker);

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = this.querySelector('[data-slides]');
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-thumb]');
      if (!thumb) return;
      this.select(thumb.dataset.thumb);
    });
    this.slides?.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.syncThumbs(), 80);
    }, { passive: true });
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
    this.syncThumbs();
  }

  step(direction) {
    const index = Math.round(this.slides.scrollLeft / this.slides.clientWidth) + direction;
    const slide = this.slides.children[index];
    if (slide) this.select(slide.dataset.mediaId);
  }

  select(mediaId) {
    const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    this.slides.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this.markThumb(mediaId);
  }

  showVariantMedia(mediaId) {
    if (mediaId) this.select(mediaId);
  }

  syncThumbs() {
    if (!this.slides) return;
    const index = Math.round(this.slides.scrollLeft / this.slides.clientWidth);
    const slide = this.slides.children[index];
    if (slide) this.markThumb(slide.dataset.mediaId);
    const counter = this.querySelector('[data-gallery-current]');
    if (counter) counter.textContent = index + 1;
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= this.slides.children.length - 1;
  }

  markThumb(mediaId) {
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.setAttribute('aria-current', String(thumb.dataset.thumb === mediaId));
    });
  }
}
customElements.define('product-gallery', ProductGallery);


class FacetForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-auto-submit], input[type="checkbox"], select')) this.submit();
    });
  }

  submit() {
    const params = new URLSearchParams(new FormData(this.form));
    for (const [key, value] of [...params.entries()]) {
      if (value === '') params.delete(key, value);
    }
    window.location.search = params.toString();
  }
}
customElements.define('facet-form', FacetForm);

class CartForm extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-auto-submit]')) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.querySelector('form')?.requestSubmit(), SEARCH_DEBOUNCE_MS);
    });
  }
}
customElements.define('cart-form', CartForm);

class ModalTrigger extends HTMLElement {
  connectedCallback() {
    const dialog = document.getElementById(this.dataset.target);
    if (!dialog) return;
    this.querySelector('button')?.addEventListener('click', () => {
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    });
    dialog.addEventListener('close', () => { document.documentElement.style.overflow = ''; });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-close-drawer]')) dialog.close();
    });
  }
}
customElements.define('modal-trigger', ModalTrigger);

class RecommendedProducts extends HTMLElement {
  connectedCallback() {
    const observer = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      const html = await (await fetch(this.dataset.url)).text();
      const fresh = parseHTML(html).querySelector('recommended-products');
      if (fresh?.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
    }, { rootMargin: '0px 0px 400px 0px' });
    observer.observe(this);
  }
}
customElements.define('recommended-products', RecommendedProducts);

document.addEventListener('click', (event) => {
  document.querySelectorAll('details[data-dismissable][open]').forEach((details) => {
    if (!details.contains(event.target)) details.removeAttribute('open');
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('details[data-dismissable][open]').forEach((details) => {
    details.removeAttribute('open');
    details.querySelector('summary')?.focus();
  });
});


const readVariantState = () => {
  try {
    return JSON.parse(document.querySelector('[data-variant-state]')?.textContent || 'null');
  } catch {
    return null;
  }
};

class ProductZoom extends HTMLElement {
  static SWIPE_THRESHOLD = 40;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.image = this.querySelector('img');
    this.counter = this.querySelector('[data-zoom-counter]');
    this.thumbs = this.querySelector('[data-zoom-thumbs]');
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-zoom]');
      if (!trigger) return;
      this.items = [...document.querySelectorAll('[data-zoom]')];
      this.returnFocus = trigger;
      this.renderThumbs();
      this.show(this.items.indexOf(trigger));
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    });
    this.image.addEventListener('click', (event) => {
      if (this.swiped) return;
      const rect = this.image.getBoundingClientRect();
      this.image.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
      this.image.classList.toggle('scale-200');
    });
    this.image.addEventListener('pointerdown', (event) => { this.startX = event.clientX; this.swiped = false; });
    this.image.addEventListener('pointerup', (event) => {
      const delta = event.clientX - (this.startX ?? event.clientX);
      if (Math.abs(delta) < ProductZoom.SWIPE_THRESHOLD || this.image.classList.contains('scale-200')) return;
      this.swiped = true;
      this.show(this.index + (delta < 0 ? 1 : -1));
    });
    this.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.thumbs?.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-zoom-index]');
      if (thumb) this.show(Number(thumb.dataset.zoomIndex));
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      if (event.key === 'ArrowRight') this.show(this.index + 1);
    });
    this.dialog.addEventListener('click', (event) => {
      const onBackdrop = event.target === this.dialog || event.target.hasAttribute('data-zoom-stage');
      if (onBackdrop || event.target.closest('[data-close-drawer]')) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      this.returnFocus?.focus();
    });
  }

  renderThumbs() {
    if (!this.thumbs) return;
    this.thumbs.hidden = this.items.length < 2;
    this.thumbs.replaceChildren(...this.items.map((item, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.zoomIndex = index;
      button.className = 'size-12 shrink-0 cursor-pointer overflow-hidden rounded-lg bg-canvas opacity-60 ring-2 ring-transparent transition md:size-14 aria-[current=true]:opacity-100 aria-[current=true]:ring-white';
      button.setAttribute('aria-label', `${index + 1} / ${this.items.length}`);
      const img = document.createElement('img');
      img.src = item.dataset.zoom.replace(/width=\d+/, 'width=120');
      img.alt = '';
      img.className = 'size-full object-cover';
      img.loading = 'lazy';
      button.append(img);
      return button;
    }));
  }

  show(index) {
    if (!this.items?.length) return;
    this.index = (index + this.items.length) % this.items.length;
    const item = this.items[this.index];
    this.image.classList.remove('scale-200');
    this.image.src = item.dataset.zoom;
    this.image.alt = item.dataset.zoomAlt || '';
    if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.items.length}`;
    const single = this.items.length < 2;
    this.querySelectorAll('[data-zoom-prev], [data-zoom-next]').forEach((button) => { button.hidden = single; });
    this.thumbs?.querySelectorAll('[data-zoom-index]').forEach((thumb, i) => {
      thumb.setAttribute('aria-current', String(i === this.index));
      if (i === this.index) this.thumbs.scrollTo({ left: thumb.offsetLeft - (this.thumbs.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
    });
  }
}
customElements.define('product-zoom', ProductZoom);

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.format = new Intl.NumberFormat(this.dataset.locale || 'nl-NL', { style: 'currency', currency: this.dataset.currency || 'EUR' });
    this.addEventListener('change', () => this.update());
    this.querySelector('[data-bundle-add]')?.addEventListener('click', () => this.add());
    document.addEventListener('variant:change', () => this.update());
    document.addEventListener('vat:change', () => this.update());
    this.update();
  }

  selected() {
    return [...this.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)')];
  }

  update() {
    const base = readVariantState();
    const basePrice = base?.price ?? 0;
    const extras = this.selected().reduce((sum, input) => sum + Number(input.dataset.price), 0);
    const set = (selector, value) => { const el = this.querySelector(selector); if (el) el.textContent = value; };
    const vatFactor = document.documentElement.dataset.vat === 'excl' ? 100 / (100 + Number(this.dataset.vatRate || 0)) : 1;
    const money = (cents) => this.format.format((cents * vatFactor) / 100);
    set('[data-bundle-base]', money(basePrice));
    set('[data-bundle-extras]', money(extras));
    set('[data-bundle-count]', this.selected().length);
    set('[data-bundle-total]', money(basePrice + extras));
    const button = this.querySelector('[data-bundle-add]');
    if (button) button.disabled = !base?.available;
  }

  async add() {
    const base = readVariantState();
    const button = this.querySelector('[data-bundle-add]');
    const errorBox = this.querySelector('[data-bundle-error]');
    if (!base?.available || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    errorBox.hidden = true;
    const items = [{ id: base.id, quantity: 1 }, ...this.selected().map((input) => ({ id: Number(input.value), quantity: 1 }))];
    try {
      const data = await fetchJSON(window.theme.routes.cartAdd + '.js', {
        method: 'POST',
        body: JSON.stringify({ items, sections: CART_SECTION_ID, sections_url: window.location.pathname }),
      });
      const drawer = document.querySelector('cart-drawer');
      if (drawer && window.theme.cartType === 'drawer') {
        drawer.render(data.sections?.[CART_SECTION_ID]);
        const count = drawer.querySelector('[data-cart-drawer-inner]')?.dataset.count;
        if (count !== undefined) updateCartCount(count);
        drawer.open(button);
        const added = toastFromAddResponse(data);
        if (added) showToast({ ...added, showCart: false });
        flashSuccess(button);
      } else {
        window.location.href = window.theme.routes.cart;
      }
    } catch (error) {
      errorBox.textContent = error.message;
      errorBox.hidden = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}
customElements.define('bundle-builder', BundleBuilder);

document.addEventListener('click', (event) => {
  const scrollTrigger = event.target.closest('[data-scroll-to]');
  if (scrollTrigger) {
    const target = document.querySelector(scrollTrigger.dataset.scrollTo);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target?.querySelector('input, select')?.focus({ preventScroll: true });
  }
  const detailsTrigger = event.target.closest('[data-open-details]');
  if (detailsTrigger) {
    const details = document.getElementById(detailsTrigger.dataset.openDetails);
    if (details) details.open = true;
  }
});

class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const now = new Date();
    const cutoff = Number(this.dataset.cutoff);
    const isWeekday = !WEEKEND_DAYS.includes(now.getDay());
    let key = 'tomorrow';
    if (isWeekday && now.getHours() < cutoff) key = 'today';
    else if (now.getDay() === 5 || now.getDay() === 6) key = 'monday';
    else if (now.getDay() === 0) key = 'tomorrow';
    const text = this.dataset[key];
    const target = this.querySelector('[data-delivery-text]');
    if (text && target) target.textContent = text;
  }
}
customElements.define('delivery-estimate', DeliveryEstimate);

class VolumeDiscount extends HTMLElement {
  connectedCallback() {
    this.format = new Intl.NumberFormat(this.dataset.locale || 'nl-NL', { style: 'currency', currency: this.dataset.currency || 'EUR' });
    this.tiers = [...this.querySelectorAll('[data-tier]')];
    this.addEventListener('click', (event) => {
      const tier = event.target.closest('[data-tier]');
      if (tier) this.select(tier);
    });
    document.addEventListener('variant:change', (event) => this.reprice(event.detail));
    document.addEventListener('change', (event) => {
      if (event.target.matches?.('[data-product-quantity]')) this.sync(Number(event.target.value));
    });
    this.sync(Number(this.quantityInput()?.value || 1));
  }

  quantityInput() {
    return document.querySelector('product-form [data-product-quantity]');
  }

  select(tier) {
    const input = this.quantityInput();
    const quantity = Number(tier.dataset.quantity);
    if (!input || !quantity) return;
    const max = input.max ? Number(input.max) : Infinity;
    input.value = Math.min(max, quantity);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this.sync(Number(input.value));
    const form = input.closest('form');
    if (form && form.getBoundingClientRect().top < 0) form.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
  }

  sync(quantity) {
    this.tiers.forEach((tier) => {
      const active = Number(tier.dataset.quantity) === quantity;
      tier.setAttribute('aria-pressed', String(active));
      const label = tier.querySelector('[data-tier-selected]');
      if (label) label.hidden = !active;
    });
  }

  totals(unit, tier) {
    const qty = Number(tier.dataset.quantity) || 1;
    const value = Number(tier.dataset.value) || 0;
    const regular = unit * qty;
    let total = regular;
    if (tier.dataset.type === 'percentage') total = Math.round((regular * (100 - Math.min(100, Math.max(0, value)))) / 100);
    else if (tier.dataset.type === 'fixed') total = Math.max(0, regular - Math.round(value * 100) * qty);
    else if (tier.dataset.type === 'x_for_y') total = unit * Math.min(qty, Math.max(1, Math.round(value)));
    return { regular, total, perPiece: Math.floor(total / qty), saving: regular - total };
  }

  reprice(state) {
    const unit = state?.price;
    if (!Number.isFinite(unit)) return;
    const vatRate = Number(this.dataset.vatRate || 0);
    const write = (root, cents) => {
      if (!root) return;
      const incl = root.querySelector('[data-vat-show="incl"]');
      const excl = root.querySelector('[data-vat-show="excl"]');
      if (incl) incl.textContent = this.format.format(cents / 100);
      if (excl) excl.textContent = this.format.format(Math.round((cents * 100) / (100 + vatRate)) / 100);
    };
    const template = this.dataset.saveTemplate || '%s';
    this.tiers.forEach((tier) => {
      const t = this.totals(unit, tier);
      write(tier.querySelector('[data-tier-total]'), t.total);
      write(tier.querySelector('[data-tier-per-piece]'), t.perPiece);
      write(tier.querySelector('[data-tier-regular]'), t.regular);
      const saving = tier.querySelector('[data-tier-saving]');
      if (saving) {
        saving.classList.toggle('invisible', t.saving <= 0);
        const incl = saving.querySelector('[data-vat-show="incl"]');
        const excl = saving.querySelector('[data-vat-show="excl"]');
        if (incl) incl.textContent = template.replace('%s', this.format.format(t.saving / 100));
        if (excl) excl.textContent = template.replace('%s', this.format.format(Math.round((t.saving * 100) / (100 + vatRate)) / 100));
      }
    });
  }
}
customElements.define('volume-discount', VolumeDiscount);

class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    const form = document.getElementById(this.dataset.target);
    if (!form) return;
    this.observer = new IntersectionObserver(([entry]) => {
      this.passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.apply();
    });
    this.inert = true;
    this.observer.observe(form);
    const suppress = () => {
      const dialogOpen = Boolean(document.querySelector('dialog[open]'));
      const typing = document.activeElement?.matches?.('input:not([type="checkbox"]):not([type="radio"]), textarea, select') && window.matchMedia('(max-width: 767px)').matches;
      this.toggleAttribute('data-suppressed', dialogOpen || typing);
      this.apply();
    };
    this.mutations = new MutationObserver(suppress);
    this.mutations.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
    document.addEventListener('focusin', suppress);
    document.addEventListener('focusout', () => setTimeout(suppress, 50));
    this.media = window.matchMedia('(max-width: 767px)');
    this.media.addEventListener('change', () => this.apply());
  }

  apply() {
    const shown = Boolean(this.passed) && !this.hasAttribute('data-suppressed') && this.media?.matches !== false;
    this.toggleAttribute('data-visible', shown);
    this.inert = !shown;
    const height = shown ? `${this.offsetHeight}px` : '0px';
    document.body.style.paddingBottom = shown ? height : '';
    // WhatsApp-float leest --sticky-bar-h en schuift mee omhoog, zodat de balk hem nooit bedekt.
    document.documentElement.style.setProperty('--sticky-bar-h', height);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mutations?.disconnect();
    document.documentElement.style.setProperty('--sticky-bar-h', '0px');
  }
}
customElements.define('sticky-buy-bar', StickyBuyBar);

const readRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const rememberProduct = (handle) => {
  try {
    const list = [handle, ...readRecent().filter((item) => item !== handle)].slice(0, RECENT_MAX_STORED);
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(list));
  } catch {}
};

class RecentlyViewed extends HTMLElement {
  async connectedCallback() {
    const handles = readRecent()
      .filter((handle) => handle !== this.dataset.exclude)
      .slice(0, Number(this.dataset.limit) || 4);
    if (!handles.length) return;
    const cards = await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(`${window.theme.routes.root}products/${encodeURIComponent(handle)}?view=recent`);
        return response.ok ? (await response.text()).trim() : '';
      } catch {
        return '';
      }
    }));
    const list = this.querySelector('[data-recent-list]');
    cards.filter(Boolean).forEach((html) => {
      const item = document.createElement('li');
      item.innerHTML = html;
      list.append(item);
    });
    this.hidden = list.children.length === 0;
  }
}
customElements.define('recently-viewed', RecentlyViewed);

const productRoot = document.querySelector('[data-product-handle]');
if (productRoot) rememberProduct(productRoot.dataset.productHandle);

const HEADER_COMPACT_AT = 120;
const HEADER_EXPAND_AT = 40;

const syncHeaderHeight = () => {
  const header = document.querySelector('.section-header header');
  if (header) document.documentElement.style.setProperty('--header-h', `${Math.round(header.getBoundingClientRect().bottom)}px`);
};

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('header');
    this.reserve();
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', () => { if (!this.compact) this.reserve(); }, { passive: true });
    this.header.addEventListener('transitionend', syncHeaderHeight);
    this.onScroll();
  }

  reserve() {
    this.style.minHeight = '';
    this.style.minHeight = `${this.offsetHeight}px`;
    syncHeaderHeight();
  }

  onScroll() {
    const y = window.scrollY;
    const next = this.compact ? y > HEADER_EXPAND_AT : y > HEADER_COMPACT_AT;
    if (next === this.compact) return;
    this.compact = next;
    this.header.toggleAttribute('data-scrolled', next);
    this.classList.toggle('pointer-events-none', next);
    this.header.classList.toggle('pointer-events-auto', next);
    requestAnimationFrame(syncHeaderHeight);
  }
}
customElements.define('sticky-header', StickyHeader);
syncHeaderHeight();

class ProductSectionNav extends HTMLElement {
  connectedCallback() {
    const sections = [...document.querySelectorAll('[data-nav-section]')];
    if (sections.length < 2) return;
    const list = this.querySelector('[data-section-nav-list]');
    const template = this.querySelector('[data-section-nav-item]');
    this.links = sections.map((section) => {
      const item = template.content.firstElementChild.cloneNode(true);
      const link = item.querySelector('a');
      link.href = `#${section.id}`;
      link.textContent = section.dataset.navSection;
      list.append(item);
      return link;
    });
    this.hidden = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this.links.forEach((link) => {
          const active = link.hash === `#${entry.target.id}`;
          link.setAttribute('aria-current', String(active));
          if (active) {
            const list = link.closest('[data-section-nav-list]');
            list?.scrollTo({ left: link.offsetLeft - list.clientWidth / 2 + link.offsetWidth / 2, behavior: 'smooth' });
          }
        });
      });
    }, { rootMargin: '-35% 0px -60% 0px' });
    sections.forEach((section) => observer.observe(section));
  }
}
customElements.define('product-section-nav', ProductSectionNav);

class SuitabilityCheck extends HTMLElement {
  connectedCallback() {
    this.copy = JSON.parse(this.querySelector('[data-check-copy]')?.textContent || '{}');
    this.addEventListener('change', () => this.evaluate());
  }

  add(kind, text) {
    const template = this.querySelector(`[data-result-${kind}]`);
    const item = template.content.firstElementChild.cloneNode(true);
    item.querySelector('[data-text]').textContent = text;
    this.querySelector('[data-check-results]').append(item);
  }

  evaluate() {
    this.querySelector('[data-check-results]').replaceChildren();
    const location = this.dataset.location || '';
    const chosen = this.querySelector('[data-check="location"] input:checked')?.value;
    if (chosen) {
      if (!location) this.add('unknown', this.copy.locationUnknown);
      else if (location.includes(chosen)) this.add('ok', this.copy.locationOk);
      else this.add('warn', this.copy.locationWarn);
    }
    const requirements = [...this.querySelectorAll('[data-check="requirements"] input')];
    if (requirements.length && requirements.some((input) => input.checked)) {
      const missing = requirements.filter((input) => !input.checked).map((input) => input.value);
      if (missing.length) this.add('warn', `${this.copy.requirementsMissing} ${missing.join(', ')}`);
      else this.add('ok', this.copy.requirementsOk);
    }
    const system = this.querySelector('[data-check="system"] select')?.value;
    if (system === 'match') this.add('ok', this.copy.systemOk);
    if (system === 'none') this.add('warn', this.copy.systemNone);
    if (system === 'unknown') this.add('unknown', this.copy.systemUnknown);
  }
}
customElements.define('suitability-check', SuitabilityCheck);

document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[data-validate]');
  if (!form) return;
  let firstInvalid = null;
  form.querySelectorAll('[data-field-error]').forEach((error) => { error.hidden = true; });
  form.querySelectorAll('input, textarea, select').forEach((field) => {
    field.removeAttribute('aria-invalid');
    if (field.checkValidity()) return;
    field.setAttribute('aria-invalid', 'true');
    const error = form.querySelector(`[data-field-error="${field.id}"]`);
    if (error) error.hidden = false;
    firstInvalid ||= field;
  });
  if (firstInvalid) {
    event.preventDefault();
    firstInvalid.focus();
    return;
  }
  form.querySelector('[type="submit"]')?.setAttribute('aria-busy', 'true');
});

const VAT_STORAGE_KEY = 'ariko:vat';
const STORE_TIMEZONE = 'Europe/Amsterdam';

class VatToggle extends HTMLElement {
  connectedCallback() {
    this.sync();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-vat-mode]');
      if (!button) return;
      const mode = button.dataset.vatMode;
      if (mode === 'excl') document.documentElement.dataset.vat = 'excl';
      else delete document.documentElement.dataset.vat;
      try { localStorage.setItem(VAT_STORAGE_KEY, mode); } catch {}
      document.querySelectorAll('vat-toggle').forEach((toggle) => toggle.sync());
      document.dispatchEvent(new CustomEvent('vat:change', { detail: { mode } }));
    });
  }

  sync() {
    const mode = document.documentElement.dataset.vat === 'excl' ? 'excl' : 'incl';
    this.querySelectorAll('[data-vat-mode]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.vatMode === mode));
    });
  }
}
customElements.define('vat-toggle', VatToggle);

class StoreStatus extends HTMLElement {
  connectedCallback() {
    let hours;
    try { hours = JSON.parse(this.dataset.hours); } catch { return; }
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: STORE_TIMEZONE, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(new Date()).map((part) => [part.type, part.value]));
    const dayIndex = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday);
    const minutesNow = Number(parts.hour) * 60 + Number(parts.minute);
    const parse = (range) => {
      const match = /^(\d{1,2})[:.](\d{2})\s*[-–]\s*(\d{1,2})[:.](\d{2})$/.exec((range || '').trim());
      if (!match) return null;
      return { open: Number(match[1]) * 60 + Number(match[2]), close: Number(match[3]) * 60 + Number(match[4]), opensAt: `${match[1].padStart(2, '0')}:${match[2]}`, closesAt: `${match[3].padStart(2, '0')}:${match[4]}` };
    };
    const today = parse(hours[dayIndex]);
    const text = this.querySelector('[data-status-text]');
    const dot = this.querySelector('[data-status-dot]');
    if (today && minutesNow >= today.open && minutesNow < today.close) {
      text.textContent = this.dataset.open.replace('{time}', today.closesAt);
      dot.dataset.state = 'open';
      return;
    }
    dot.dataset.state = 'closed';
    const lang = document.documentElement.lang || 'nl';
    const dayNames = new Intl.DateTimeFormat(lang, { weekday: 'long' });
    const relative = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    for (let offset = 0; offset < 7; offset += 1) {
      const index = (dayIndex + offset) % 7;
      const slot = parse(hours[index]);
      if (!slot || (offset === 0 && minutesNow >= slot.open)) continue;
      const reference = new Date(Date.UTC(2024, 0, 7 + index));
      const day = offset <= 1 ? relative.format(offset, 'day') : dayNames.format(reference);
      text.textContent = this.dataset.opens.replace('{day}', day).replace('{time}', slot.opensAt);
      return;
    }
    text.textContent = this.dataset.closed;
  }
}
customElements.define('store-status', StoreStatus);

class TabGroup extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll(':scope > [role="tablist"] [role="tab"]')];
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
    this.querySelector('[role="tablist"]')?.addEventListener('keydown', (event) => {
      const step = { ArrowRight: 1, ArrowLeft: -1, Home: -Infinity, End: Infinity }[event.key];
      const index = this.tabs.indexOf(document.activeElement);
      if (step === undefined || index === -1) return;
      event.preventDefault();
      const target = step === -Infinity ? 0 : step === Infinity ? this.tabs.length - 1 : (index + step + this.tabs.length) % this.tabs.length;
      this.select(this.tabs[target]);
      this.tabs[target].focus();
    });
  }

  select(active) {
    this.tabs.forEach((tab) => {
      const selected = tab === active;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
    const list = active.parentElement;
    list.scrollTo({ left: active.offsetLeft - list.clientWidth / 2 + active.offsetWidth / 2, behavior: 'smooth' });
  }
}
customElements.define('tab-group', TabGroup);

if (/contact_posted=true|form_type=contact/.test(window.location.search) || window.location.hash === '#AdvicePopupForm') {
  const adviceDialog = document.getElementById('AdviceDialog');
  if (adviceDialog && (window.location.hash === '#AdvicePopupForm' || adviceDialog.querySelector('[role="status"], [role="alert"]:not([hidden])'))) {
    adviceDialog.showModal();
    document.documentElement.style.overflow = 'hidden';
  }
}

const LOCALIZATION_DISMISS_KEY = 'ariko:localization-dismissed';
const LOCALIZATION_MIN_CONFIDENCE = 0.5;

class LocalizationSuggestion extends HTMLElement {
  async connectedCallback() {
    const current = `${this.dataset.country}|${this.dataset.language}`;
    try { if (localStorage.getItem(LOCALIZATION_DISMISS_KEY) === current) return; } catch {}
    const params = new URLSearchParams({
      'country[enabled]': 'true',
      'country[exclude]': this.dataset.country,
      'language[enabled]': 'true',
      'language[exclude]': this.dataset.language,
    });
    let data;
    try {
      const response = await fetch(`${window.theme.routes.root}browsing_context_suggestions.json?${params}`);
      if (!response.ok) return;
      data = await response.json();
    } catch {
      return;
    }
    const parts = data?.suggestions?.[0]?.parts;
    if (!parts) return;
    const country = parts.country?.confidence >= LOCALIZATION_MIN_CONFIDENCE ? parts.country : null;
    const language = parts.language?.confidence >= LOCALIZATION_MIN_CONFIDENCE ? parts.language : null;
    if (!country && !language) return;
    if (country) this.querySelector('[data-suggest-country]').value = country.handle;
    if (language) this.querySelector('[data-suggest-language]').value = language.handle;
    const label = [country?.name, language?.name].filter(Boolean).join(' · ');
    this.querySelector('[data-suggest-text]').textContent = this.querySelector('[data-suggest-template]').innerHTML.trim().replace('{target}', label);
    this.hidden = false;
    this.querySelector('[data-suggest-dismiss]').addEventListener('click', () => {
      try { localStorage.setItem(LOCALIZATION_DISMISS_KEY, current); } catch {}
      this.hidden = true;
    });
  }
}
customElements.define('localization-suggestion', LocalizationSuggestion);

/* Taal-/landkiezer als <dialog>: bottom sheet op mobiel (sleep omlaag om te sluiten),
   gecentreerde modal op desktop. Submit blijft de gewone {% form 'localization' %}. */
class LocalizationSheet extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-sheet-open]');
      if (opener) return this.open(document.getElementById(opener.dataset.sheetOpen), opener);
      const dialog = event.target.closest('dialog.sheet');
      if (!dialog) return;
      if (event.target === dialog || event.target.closest('[data-sheet-close]')) this.close(dialog);
    });
    this.querySelectorAll('dialog.sheet').forEach((dialog) => {
      dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        this.querySelector(`[data-sheet-open="${dialog.id}"]`)?.setAttribute('aria-expanded', 'false');
        dialog.returnFocus?.focus?.();
        dialog.returnFocus = null;
      });
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close(dialog);
      });
      this.enableDrag(dialog);
    });
  }

  open(dialog, trigger) {
    if (!dialog || dialog.open) return;
    dialog.returnFocus = trigger || document.activeElement;
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    trigger?.setAttribute('aria-expanded', 'true');
    dialog.querySelector('[data-filter-list]')?.focus({ preventScroll: true });
  }

  close(dialog) {
    if (!dialog?.open) return;
    dialog.style.removeProperty('--sheet-drag');
    dialog.classList.remove('is-dragging');
    dialog.close();
  }

  enableDrag(dialog) {
    let startY = 0;
    let delta = 0;
    let pointerId = null;
    const grips = dialog.querySelectorAll('[data-sheet-grip]');
    const isMobile = () => window.matchMedia('(max-width: 767px)').matches;
    const end = () => {
      if (pointerId === null) return;
      pointerId = null;
      dialog.classList.remove('is-dragging');
      dialog.style.removeProperty('--sheet-drag');
      if (delta > Math.min(120, dialog.offsetHeight * 0.3)) this.close(dialog);
      delta = 0;
    };
    grips.forEach((grip) => {
      grip.addEventListener('pointerdown', (event) => {
        if (!isMobile() || event.target.closest('button')) return;
        pointerId = event.pointerId;
        startY = event.clientY;
        delta = 0;
        dialog.classList.add('is-dragging');
        grip.setPointerCapture?.(pointerId);
      });
      grip.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        delta = Math.max(0, event.clientY - startY);
        dialog.style.setProperty('--sheet-drag', `${delta}px`);
      });
      grip.addEventListener('pointerup', end);
      grip.addEventListener('pointercancel', end);
    });
  }
}
customElements.define('localization-sheet', LocalizationSheet);

document.addEventListener('input', (event) => {
  const filter = event.target.closest('[data-filter-list]');
  if (!filter) return;
  const term = filter.value.trim().toLowerCase();
  document.getElementById(filter.dataset.filterList)?.querySelectorAll('[data-filter-text]').forEach((item) => {
    item.hidden = Boolean(term) && !item.dataset.filterText.includes(term);
  });
});

class MenuDrawerNav extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-menu-root]');
    this.stack = [];
    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-open-panel]');
      if (opener) {
        this.open(opener);
        return;
      }
      if (event.target.closest('[data-close-panel]')) this.back();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' && this.stack.length) {
        event.preventDefault();
        this.back();
      }
    });
    this.closest('dialog')?.addEventListener('close', () => this.reset());
  }

  open(opener) {
    const panel = document.getElementById(opener.getAttribute('aria-controls'));
    if (!panel) return;
    const current = this.stack.at(-1)?.panel || this.root;
    current.setAttribute('data-covered', '');
    current.inert = true;
    panel.inert = false;
    panel.setAttribute('data-open', '');
    opener.setAttribute('aria-expanded', 'true');
    this.stack.push({ panel, opener });
    panel.scrollTop = 0;
    panel.querySelector('[data-close-panel]')?.focus({ preventScroll: true });
  }

  back() {
    const entry = this.stack.pop();
    if (!entry) return;
    entry.panel.removeAttribute('data-open');
    entry.panel.inert = true;
    entry.opener.setAttribute('aria-expanded', 'false');
    const previous = this.stack.at(-1)?.panel || this.root;
    previous.removeAttribute('data-covered');
    previous.inert = false;
    entry.opener.focus({ preventScroll: true });
  }

  reset() {
    while (this.stack.length) {
      const entry = this.stack.pop();
      entry.panel.removeAttribute('data-open');
      entry.panel.inert = true;
      entry.opener.setAttribute('aria-expanded', 'false');
    }
    this.root.removeAttribute('data-covered');
    this.root.inert = false;
    this.querySelectorAll('[data-covered]').forEach((el) => el.removeAttribute('data-covered'));
  }
}
customElements.define('menu-drawer-nav', MenuDrawerNav);

document.addEventListener('scroll', (event) => {
  const slides = event.target instanceof Element && event.target.matches('[data-card-slides]') ? event.target : null;
  if (!slides) return;
  const index = Math.round(slides.scrollLeft / slides.clientWidth);
  slides.parentElement.querySelectorAll('[data-card-dots] > span').forEach((dot, i) => dot.toggleAttribute('data-active', i === index));
}, { capture: true, passive: true });

const CAROUSEL_MIN_AUTOPLAY_S = 3;

class CarouselSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.dots = this.querySelector('[data-carousel-dots]');
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.update(), 60);
    }, { passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.go(1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(-1); }
    });
    new ResizeObserver(() => this.build()).observe(this.track);
    new MutationObserver(() => this.build()).observe(this.track, { childList: true });
    this.build();
    this.setupAutoplay();
  }

  pages() {
    return Math.max(1, Math.ceil((this.track.scrollWidth - 2) / this.track.clientWidth));
  }

  current() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (max <= 0) return 0;
    if (this.track.scrollLeft >= max - 2) return this.pages() - 1;
    return Math.round(this.track.scrollLeft / this.track.clientWidth);
  }

  go(step) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    let target = this.track.scrollLeft + step * this.track.clientWidth;
    if (this.loop && step > 0 && this.track.scrollLeft >= max - 2) target = 0;
    this.track.scrollTo({ left: Math.max(0, Math.min(max, target)) });
  }

  build() {
    if (this.dots) {
      const template = this.querySelector('[data-carousel-dot]');
      const count = this.pages();
      if (this.dots.children.length !== count && template) {
        this.dots.replaceChildren(...Array.from({ length: count }, (_, i) => {
          const dot = template.content.firstElementChild.cloneNode(true);
          dot.setAttribute('aria-label', `${i + 1} / ${count}`);
          dot.addEventListener('click', () => this.track.scrollTo({ left: i * this.track.clientWidth }));
          return dot;
        }));
      }
      this.dots.hidden = count < 2;
    }
    this.update();
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const scrollable = max > 2;
    this.querySelector('[data-carousel-controls]')?.toggleAttribute('hidden', !scrollable);
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = !this.loop && this.track.scrollLeft >= max - 2;
    const index = this.current();
    [...(this.dots?.children || [])].forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
  }

  setupAutoplay() {
    const seconds = Number(this.dataset.autoplay || 0);
    if (!seconds || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.loop = true;
    const interval = Math.max(CAROUSEL_MIN_AUTOPLAY_S, seconds) * 1000;
    let paused = false;
    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
    this.addEventListener('touchstart', pause, { passive: true });
    setInterval(() => {
      if (!paused && !document.hidden && this.isConnected) this.go(1);
    }, interval);
    this.update();
  }
}
customElements.define('carousel-slider', CarouselSlider);

